import {
  Button,
  Dropdown,
  mergeClasses,
  Option,
  SearchBox,
  Spinner,
  Tooltip,
} from "@fluentui/react-components";
import { SkeletonTable } from "components/skeletonTable/SkeletonTable";
import { AdminTable } from "./AdminTable";
import { AdminPaginationFooter } from "./AdminPaginationFooter";
import { useAdminListPageStyles } from "./AdminListPage.styles";
import { IAdminListPageProps } from "./AdminListPage.types";
import {
  useSharedButtonStyles,
  useSharedSearchBoxStyles,
  useSharedDropdownStyles,
} from "theme/sharedStyles";

export const AdminListPage = <T,>(
  props: IAdminListPageProps<T>,
): JSX.Element => {
  const {
    items,
    columns,
    isLoading,
    headerActions,
    searchPlaceholder = "Search...",
    searchQuery,
    onSearchChange,
    filters,
    rowActions,
    getRowId,
    sortColumn,
    isSortDescending,
    onSortChange,
    onSortClear,
    paginatedItems = items,
    selectedPageIndex = 0,
    pageCount = 1,
    totalItemsCount = items.length,
    itemsPerPage = 10,
    onPageChange,
    emptyStateMessage,
    shimmerRows = 5,
    hideHeader,
    actionsWidth,
    actionsHeaderLabel,
    highlightedRowId,
    indentFirstColumn,
    hint,
    noPadding,
    selectable,
    selectedRowIds,
    onSelectionChange,
    getRowLabel,
  } = props;

  const styles = useAdminListPageStyles();
  const buttonStyles = useSharedButtonStyles();
  const searchBoxStyles = useSharedSearchBoxStyles();
  const dropdownStyles = useSharedDropdownStyles();

  const showPagination = pageCount > 1 && onPageChange;
  const hasHeader =
    !hideHeader &&
    (onSearchChange ||
      (filters && filters.length > 0) ||
      (headerActions && headerActions.length > 0));

  return (
    <div className={styles.container}>
      {hasHeader && (
        <div
          className={mergeClasses(
            styles.header,
            noPadding && styles.headerNoPadding,
          )}
        >
          {onSearchChange && (
            <div className={styles.searchBoxContainer}>
              <SearchBox
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={onSearchChange}
                className={searchBoxStyles.searchBox}
              />
            </div>
          )}

          {filters && filters.length > 0 && (
            <div className={styles.filterContainer}>
              {filters.map((filter) => (
                <Dropdown
                  key={filter.label}
                  positioning="below"
                  aria-label={filter.label}
                  value={
                    filter.options.find((opt) => opt.key === filter.selectedKey)
                      ?.label || filter.label
                  }
                  onOptionSelect={(_event, data) => {
                    filter.onChange(data.optionValue || filter.selectedKey);
                  }}
                  className={mergeClasses(
                    dropdownStyles.dropdown,
                    filter.className,
                  )}
                  listbox={{
                    className: mergeClasses(
                      dropdownStyles.listbox,
                      filter.listboxClassName,
                    ),
                  }}
                >
                  {filter.options.map((option) => (
                    <Option
                      key={option.key}
                      value={option.key}
                      text={option.label}
                    >
                      {option.label}
                    </Option>
                  ))}
                </Dropdown>
              ))}
            </div>
          )}

          {headerActions && headerActions.length > 0 && (
            <div className={styles.headerActions}>
              {headerActions.map((action) => {
                const button = (
                  <Button
                    key={action.tooltip ? undefined : action.label}
                    appearance="secondary"
                    onClick={action.onClick}
                    disabled={action.disabled || action.loading}
                    icon={
                      action.loading ? <Spinner size="tiny" /> : action.icon
                    }
                    className={
                      action.appearance === "primary"
                        ? buttonStyles.primaryButton
                        : buttonStyles.secondaryButton
                    }
                  >
                    {action.label}
                  </Button>
                );
                return action.tooltip ? (
                  <Tooltip
                    key={action.label}
                    content={action.tooltip}
                    relationship="label"
                  >
                    {button}
                  </Tooltip>
                ) : (
                  button
                );
              })}
            </div>
          )}
        </div>
      )}

      <div
        className={mergeClasses(styles.body, noPadding && styles.bodyNoPadding)}
      >
        {isLoading ? (
          <SkeletonTable
            columnCount={
              columns.length + (rowActions ? 1 : 0) + (selectable ? 1 : 0)
            }
            rows={shimmerRows}
          />
        ) : (
          <div className={styles.tableContainer}>
            <AdminTable<T>
              items={paginatedItems}
              allItems={items}
              columns={columns}
              getRowId={getRowId}
              rowActions={rowActions}
              sortColumn={sortColumn}
              isSortDescending={isSortDescending}
              onSortChange={onSortChange}
              onSortClear={onSortClear}
              emptyStateMessage={emptyStateMessage}
              actionsWidth={actionsWidth}
              actionsHeaderLabel={actionsHeaderLabel}
              highlightedRowId={highlightedRowId}
              indentFirstColumn={indentFirstColumn}
              selectable={selectable}
              selectedRowIds={selectedRowIds}
              onSelectionChange={onSelectionChange}
              getRowLabel={getRowLabel}
            />

            {showPagination && (
              <div>
                <AdminPaginationFooter
                  selectedIndex={selectedPageIndex}
                  pageCount={pageCount}
                  itemsPerPage={itemsPerPage}
                  totalItemsCount={totalItemsCount}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </div>
        )}
        {hint}
      </div>
    </div>
  );
};
